import React from 'react';
import { Box, Card, Chip, Container, Typography } from '@mui/material';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import StoreIcon from '@mui/icons-material/Store';

import visaLogo from '../../../../assets/visaLogo.png';
import masterLogo from '../../../../assets/masterLogo.png';
import amexLogo from '../../../../assets/amexLogo.png';
import itauLogo from '../../../../assets/itau-logo.png';
import { useAgencyBranding } from '../../branding';

import {
	logoBoxSx,
	logoGridSx,
	paymentCardSx,
	paymentIntroSx,
	paymentMethodChipSx,
	paymentNoteSx,
	paymentSectionTitleSx,
} from './sharedStyles';

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		root: {
			flexGrow: 1,
			paddingTop: theme.spacing(2),
			maxWidth: '100%',
		},
		paymentMethodsGrid: {
			display: 'flex',
			flexWrap: 'wrap',
			gap: theme.spacing(1),
			marginTop: theme.spacing(1),
			marginBottom: theme.spacing(1),
		},
		paymentIcon: {
			marginRight: theme.spacing(0.5),
			color: theme.palette.secondary.main,
		},
		itauLogo: {
			width: '48px',
			borderRadius: 10,
			flex: '0 0 auto',
		},
		noUnderlineLinks: {
			textDecoration: 'none',
			color: 'inherit',
		},
	}),
);

interface ItauPYPaymentMethodsProps {
	isExternal?: boolean;
}

const ItauPYPaymentMethods = ({ isExternal }: ItauPYPaymentMethodsProps): JSX.Element => {
	const classes = useStyles();
	const branding = useAgencyBranding(isExternal);

	const paymentMethods = [
		{
			label: 'Tarjetas de crédito y débito',
			icon: <CreditCardIcon className={classes.paymentIcon} fontSize="small" />,
		},
		{ label: 'Puntos Itaú', icon: <StoreIcon className={classes.paymentIcon} fontSize="small" /> },
		{ label: 'Tarjeta + Puntos', icon: <AccountBalanceIcon className={classes.paymentIcon} fontSize="small" /> },
	];
	const cardLogos = [
		{ logo: visaLogo, alt: 'visa' },
		{ logo: amexLogo, alt: 'american-express' },
		{ logo: masterLogo, alt: 'master-card' },
	];

	return (
		<div className={classes.root}>
			<Container component="main" maxWidth="md">
				<Typography component="h1" variant="h6" sx={paymentSectionTitleSx(isExternal, branding.headerGradient)}>
					Nuestras formas de pago:
				</Typography>
				<Typography variant="body2" color="text.secondary" fontWeight={500} sx={paymentIntroSx}>
					Elegí la forma de pago que mejor se adapte a tu viaje.
				</Typography>
				<Card sx={paymentCardSx}>
					<Typography variant="body2" fontWeight={800} color="primary">
						Medios disponibles
					</Typography>
					<Box className={classes.paymentMethodsGrid}>
						{paymentMethods.map((method) => (
							<Chip key={method.label} icon={method.icon} label={method.label} size="small" sx={paymentMethodChipSx} />
						))}
					</Box>
					<Typography variant="body2" fontWeight={800} color="primary" mt={1.5}>
						Tarjetas
					</Typography>
					<Box sx={logoGridSx}>
						{cardLogos.map((card) => (
							<Box key={card.alt} sx={logoBoxSx}>
								<img src={card.logo} alt={card.alt} />
							</Box>
						))}
					</Box>
					<Typography variant="body2" fontWeight={800} color="primary" mt={2} mb={1}>
						Aprovechá nuestros beneficios directos
					</Typography>
					{/* Beneficio Itaú con el logo nuevo: card clara con acento naranja,
					    en línea con las filas de beneficio del rediseño. */}
					<a
						className={classes.noUnderlineLinks}
						href="https://www.itau.com.py/Beneficios/Detalle/6062-agenciadeviajes"
						target="_blank"
						rel="noreferrer"
					>
						<Box
							sx={{
								backgroundColor: '#fff',
								borderRadius: '10px',
								border: '2px solid #EE7000',
								boxShadow: '0 4px 12px rgba(238, 112, 0, 0.16)',
								display: 'flex',
								alignItems: 'center',
								gap: 1.5,
								p: 1.5,
							}}
						>
							<img src={itauLogo} alt="Itaú" className={classes.itauLogo} />
							<Box minWidth={0}>
								<Typography variant="body1" fontWeight={800} lineHeight={1.2}>
									Hasta 15 cuotas sin intereses
								</Typography>
								<Typography variant="caption" color="text.secondary">
									Con tarjetas de crédito Itaú · Ver bases y condiciones
								</Typography>
							</Box>
						</Box>
					</a>
					<Typography sx={paymentNoteSx} variant="body2" align="center" color="text.secondary" fontWeight={700}>
						Tarjetas de crédito de otros bancos en un solo pago sin interés o en cuotas con recargo. Consultá con tu
						banco.
					</Typography>
				</Card>
			</Container>
		</div>
	);
};

export default ItauPYPaymentMethods;
